import Alpine from 'alpinejs';
import Swiper from 'swiper';
import {
    Autoplay
} from 'swiper/modules';
import 'swiper/css';

Alpine.data('parallaxImageBreaker', () => ({
    swiper: null,
    mq: null,
    boundResize: null,
    boundScroll: null,
    ticking: false,
    cards: [],
    cardCount: 0,

    init() {
        this.mq = window.matchMedia('(min-width: 768px)');
        this.boundResize = () => this.reconcile();
        this.boundScroll = () => this.onScroll();
        this.mq.addEventListener('change', this.boundResize);
        this.$nextTick(() => this.reconcile());
    },

    destroy() {
        if (this.mq && this.boundResize) {
            this.mq.removeEventListener('change', this.boundResize);
        }
        window.removeEventListener('scroll', this.boundScroll);
        this.destroySwiper();
    },

    reconcile() {
        if (this.mq.matches) {
            this.destroySwiper();
            this.$nextTick(() => this.initDesktopParallax());
        } else {
            this.teardownDesktopParallax();
            this.$nextTick(() => this.initSwiper());
        }
    },

    initDesktopParallax() {
        this.teardownDesktopParallax();
        const cardEls = this.$el.querySelectorAll('[data-card]');
        if (!cardEls.length) {
            return;
        }
        this.cards = Array.from(cardEls).map((cardEl) => ({
            cardEl,
            imageWrapper: cardEl.querySelector('[data-parallax-image-wrapper]'),
        })).filter((card) => !!card.imageWrapper);
        this.cardCount = this.cards.length;
        if (!this.cardCount) {
            return;
        }
        window.addEventListener('scroll', this.boundScroll, {
            passive: true
        });
        this.updateDesktopParallax();
    },

    teardownDesktopParallax() {
        window.removeEventListener('scroll', this.boundScroll);
        this.cards.forEach((card) => {
            card.cardEl.style.transform = 'translate3d(0, 0, 0)';
            card.imageWrapper.style.transform = 'translate3d(0, 0, 0)';
        });
        this.cards = [];
        this.cardCount = 0;
        this.ticking = false;
    },

    onScroll() {
        if (this.ticking) {
            return;
        }
        this.ticking = true;
        requestAnimationFrame(() => {
            this.updateDesktopParallax();
            this.ticking = false;
        });
    },

    getCardDriftDirection(index) {
        if (this.cardCount === 1) {
            return 0;
        }
        if (this.cardCount === 2) {
            return index === 0 ? -1 : 1;
        }
        // 3 cards: left up, middle static, right down
        if (index === 0) return -1;
        if (index === 1) return 0;
        return 1;
    },

    updateDesktopParallax() {
        const vh = window.innerHeight || 1;
        const imageShiftMax = 80; // subtle image parallax
        const cardShiftMax = 40; // subtle card drift

        this.cards.forEach((card, index) => {
            const rect = card.cardEl.getBoundingClientRect();
            const cardCenterY = rect.top + rect.height / 2;
            const progressRaw = (cardCenterY - vh / 2) / (vh / 2);
            const progress = Math.max(-1, Math.min(1, progressRaw));

            const imageOffset = -progress * imageShiftMax;
            card.imageWrapper.style.transform = `translate3d(0, ${imageOffset}px, 0)`;

            const driftDirection = this.getCardDriftDirection(index);
            const cardOffset = driftDirection * progress * cardShiftMax;
            card.cardEl.style.transform = `translate3d(0, ${cardOffset}px, 0)`;
        });
    },

    initSwiper() {
        this.destroySwiper();
        const el = this.$refs.mobileSwiper;
        if (!el) {
            return;
        }
        const slideCount = el.querySelectorAll('.swiper-slide').length;
        const originalCount = parseInt(String(el.dataset.originalSlideCount || '0'), 10) || slideCount;
        // Swiper 12 loopFix requires slides.length >= slidesPerViewDynamic + loopedSlides; with
        // centeredSlides + auto width that often fails for only 2–6 slides. Two real slides: rewind instead of loop.
        const useRewindNotLoop = originalCount === 2 && slideCount > 1;
        try {
            this.swiper = new Swiper(el, {
                modules: [Autoplay],
                slidesPerView: 'auto',
                centeredSlides: true,
                centeredSlidesBounds: false,
                spaceBetween: 20,
                speed: 400,
                roundLengths: true,
                // watchOverflow locks swiping when Swiper miscalculates overflow (common with centered + auto width)
                watchOverflow: false,
                loop: slideCount > 1 && !useRewindNotLoop,
                rewind: useRewindNotLoop,
                loopAdditionalSlides: slideCount > 1 && !useRewindNotLoop ? 2 : 0,
                autoplay: slideCount > 1 ?
                    {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    } :
                    false,
            });
        } catch (e) {
            console.warn('ParallaxImageBreaker: Swiper init failed', e);
        }
    },

    destroySwiper() {
        if (this.swiper && typeof this.swiper.destroy === 'function') {
            this.swiper.destroy(true, true);
        }
        this.swiper = null;
    },
}));